import { ChevronDownIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSession } from '@/lib/auth/session';
import { cn } from '@/lib/utils';
import { AccountMenuItems } from './AccountMenuItems';
import { initialsOf } from './accountIdentity';
import { UserAvatar } from './UserAvatar';

/**
 * The signed-in person on a page with no shell to put them in.
 *
 * The workspace sidebar carries `SignedInPerson`, which has room for a name and
 * an address on the row itself. The front door has neither a sidebar nor a top
 * bar, so identity collapses to initials and the name moves inside the menu —
 * which is why this passes `showIdentity`, and the sidebar's copy does not.
 *
 * `null` is a real state rather than an error: it is the moment before the
 * session resolves, and what any test that mounts no session provider sees. The
 * trigger is disabled rather than hidden so the page does not change shape as
 * the session lands.
 */
export function AccountMenu({ className }: { className?: string }) {
  const { user } = useSession();
  const initials = user ? initialsOf(user.displayName, user.email) : '–';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={!user}
        aria-label="Account menu"
        className={cn(
          'text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring flex items-center gap-1.5 rounded-full py-1 pr-2 pl-1 transition-colors pointer-coarse:min-h-11 focus-visible:ring-3 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
      >
        {/* The same disc this person gets on a task card and at the foot of the
            rail. It was `bg-muted` on the reasoning that `bg-primary` already
            means "a workspace" on this page — which was right about `bg-primary`
            and wrong to answer it with grey: the tint is keyed on the person's
            id, from the label slots, so it cannot be mistaken for the workspace
            fill either. Decorative, because the trigger it sits inside is what
            assistive technology announces. */}
        {user ? (
          <UserAvatar
            id={user.id}
            displayName={user.displayName}
            email={user.email}
            className="size-7 text-xs"
          />
        ) : (
          <span
            aria-hidden="true"
            className="bg-muted text-foreground flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-medium"
          >
            {initials}
          </span>
        )}
        <ChevronDownIcon
          aria-hidden="true"
          className="size-4"
          strokeWidth={1.5}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <AccountMenuItems showIdentity />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
